import {
  AlertOwnerState,
  autocompleteClasses,
  AutocompleteOwnerState,
  ButtonGroupOwnerState,
  ButtonOwnerState,
  CardOwnerState,
  ChipOwnerState,
  drawerClasses,
  formControlClasses,
  formLabelClasses,
  inputClasses,
  InputOwnerState,
  linkClasses,
  ListOwnerState,
  modalCloseClasses,
  ModalDialogOwnerState,
  selectClasses,
  SwitchOwnerState,
  tabClasses,
  tabPanelClasses,
  TextareaOwnerState,
  Theme,
  TooltipOwnerState,
  typographyClasses,
} from '@mui/joy';
import { CSSInterpolation } from '@mui/material';

import { Color, CustomColor, ModeType, Size, Variant } from './enums';

export const styleOverridesButton = (ownerState: ButtonOwnerState, theme: Theme) => ({
  ...(ownerState.color === Color.NEUTRAL &&
    ownerState.variant === Variant.OUTLINED && {
      backgroundColor: theme.palette.neutral.softBg,
      border: `1px solid ${theme.palette.neutral.softOutlined}`,
    }),

  ...(ownerState.color === Color.WARNING &&
    ownerState.variant === Variant.SOFT && {
      backgroundColor: theme.palette.warning[200],
      color: theme.palette.warning[700],

      '&:hover': {
        backgroundColor: theme.palette.warning.softBg,
      },
    }),

  svg: {
    height: 18,
    width: 18,
  },
});

export const styleOverridesGroupButton = (ownerState: ButtonGroupOwnerState, theme: Theme) => ({
  ...(ownerState.color === Color.NEUTRAL &&
    ownerState.variant === Variant.OUTLINED && {
      '--ButtonGroup-separatorColor': theme.palette.neutral.softOutlined,
    }),
});

export const styleOverridesInput = (
  ownerState: InputOwnerState | TextareaOwnerState | AutocompleteOwnerState<any, any, any, any>,
  theme: Theme
) => {
  return {
    boxShadow: 'none',
    borderRadius: 3,
    fontWeight: 400,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.body,

    ...(ownerState.color === Color.NEUTRAL && {
      borderColor: theme.palette.ag.input.borderColor,
    }),

    ...(ownerState.color === Color.WARNING && {
      borderColor: theme.palette.warning[400],
      color: theme.palette.warning[400],

      ...(theme.palette.mode === ModeType.DARK && {
        borderColor: theme.palette.warning[600],
        color: theme.palette.warning[600],
      }),
    }),

    ...(ownerState.color === Color.DANGER && {
      color: theme.palette.danger[500],
    }),

    ...(ownerState.size === Size.SM && {
      fontSize: 13,
    }),

    [`&.${inputClasses.disabled}`]: {
      backgroundColor: theme.palette.ag.input.disabledBackgroundColor,
      borderColor: theme.palette.ag.input.disabledBorderColor,
    },
  };
};

export const styleOverridesAutocomplete = (theme: Theme) => ({
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderRadius: 3,
  backgroundColor: theme.palette.background.body,

  // eslint-disable-next-line max-len
  [`&.${autocompleteClasses.colorNeutral}.${autocompleteClasses.variantOutlined}:not(.${autocompleteClasses.disabled})`]:
    {
      borderColor: theme.palette.ag.input.borderColor,
    },

  [`&.${autocompleteClasses.disabled}`]: {
    backgroundColor: theme.palette.ag.input.disabledBackgroundColor,
    borderColor: theme.palette.ag.input.disabledBorderColor,
  },

  [`&.${autocompleteClasses.sizeSm}`]: {
    fontSize: 13,
  },
});

export const styleOverridesAutocompleteListbox = (theme: Theme) => ({
  gap: 10,
  borderRadius: 3,
  backgroundColor: theme.palette.background.body,
  borderColor: theme.palette.ag.input.borderColor,
  boxShadow: '0px 2px 4px 0px rgba(21, 21, 21, 0.08), 0px 1px 2px 0px rgba(21, 21, 21, 0.08)',
  color: theme.palette.neutral[600],
  fontSize: 13,

  ".MuiAutocomplete-option[aria-disabled='true']": {
    opacity: 0.6,
    backgroundColor: theme.palette.background.surface,
  },
});

export const styleOverridesChip = (ownerState: ChipOwnerState, theme: Theme) => ({
  ...(ownerState.color === Color.WARNING &&
    ownerState.variant === Variant.SOFT && {
      color: theme.palette.neutral[800],
    }),
  // Fix outlined neutral chip visibility in dark mode
  ...(ownerState.variant === Variant.OUTLINED &&
    (!ownerState.color || ownerState.color === Color.NEUTRAL) && {
      color: theme.palette.text.primary,
      borderColor: theme.palette.neutral[400],
    }),
});

export const styleOverridesMenu = () => ({
  borderRadius: 4,
  boxShadow:
    '0px 5px 14px 0px rgba(0, 0, 0, 0.12), ' +
    '0px 9px 10px 0px rgba(0, 0, 0, 0.14), ' +
    '0px 5px 5px 0px rgba(0, 0, 0, 0.20)',
  border: 'none',
});

export const styleOverridesDrawer = (theme: Theme) => ({
  [`&.${drawerClasses.colorNeutral} > .${drawerClasses.content}`]: {
    backgroundColor: theme.palette.background.body,
  },

  [`&.${drawerClasses.colorPrimary} > .${drawerClasses.content}`]: {
    backgroundColor: theme.palette.background.surface,
  },

  [`.${drawerClasses.content}`]: {
    padding: '0 24px',
    boxShadow: '0px -40px 11px -2px rgba(21, 21, 21, 0.22), 0px -3px 6px 0px rgba(0, 0, 0, 0.07)',

    [`.${modalCloseClasses.root}`]: {
      top: 27,
    },
  },

  [`.${drawerClasses.backdrop}`]: {
    backdropFilter: 'none',
  },
});

export const styleOverridesCheckbox = () => ({
  display: 'flex',
  alignItems: 'center',
});

export const styleOverridesTabs = () => ({
  backgroundColor: 'inherit',
});

export const styleOverridesTab = (theme: Theme) => ({
  color: theme.palette.neutral[600],
  padding: '10px 16px',
  fontWeight: 500,

  [`&.${tabClasses.selected}`]: {
    backgroundColor: 'inherit',
    color: theme.palette.primary.softColor,
  },

  ':hover': {
    backgroundColor: 'inherit !important',
    color: theme.palette.primary.softColor,
  },
});

export const styleOverridesTabPanel = (): CSSInterpolation => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '24px 0',

  [`&.${tabPanelClasses.hidden}`]: {
    display: 'none',
  },
});

export const styleOverridesFormControl = () => ({
  [`&.${formControlClasses.sizeSm}`]: {
    [`& .${formLabelClasses.root}`]: {
      fontSize: 14,
    },
  },
  [`&.${formControlClasses.sizeMd}`]: {
    [`& .${formLabelClasses.root}`]: {
      fontSize: 16,
    },
  },
  [`&.${formControlClasses.sizeLg}`]: {
    [`& .${formLabelClasses.root}`]: {
      fontSize: 20,
    },
  },
});

export const styleOverridesFormLabel = (theme: Theme) => ({
  color:
    theme.palette.mode === ModeType.DARK
      ? theme.palette.text.secondary
      : theme.palette.neutral[700],
  marginBottom: 8,
});

export const styleOverridesSelect = (theme: Theme) => ({
  boxShadow: 'none',
  borderRadius: 3,
  backgroundColor: theme.palette.background.body,
  color: theme.palette.text.primary,

  // eslint-disable-next-line max-len
  [`&.${selectClasses.colorNeutral}.${selectClasses.variantOutlined}:not(.${selectClasses.disabled})`]:
    {
      borderColor: theme.palette.ag.input.borderColor,

      ':focus-within::before': {
        boxShadow: `inset 0 0 0 2px ${theme.palette.focusVisible}`,
      },
    },

  [`&.${selectClasses.disabled}`]: {
    backgroundColor: theme.palette.ag.input.disabledBackgroundColor,
    borderColor: theme.palette.ag.input.disabledBorderColor,
  },

  [`&.${selectClasses.sizeSm}`]: {
    fontSize: 13,
  },
});

export const styleOverridesSelectListbox = (theme: Theme) => ({
  gap: 10,
  borderRadius: 3,
  backgroundColor: theme.palette.background.body,
  borderColor: theme.palette.ag.input.borderColor,
  boxShadow: '0px 2px 4px 0px rgba(21, 21, 21, 0.08), 0px 1px 2px 0px rgba(21, 21, 21, 0.08)',
  color: theme.palette.neutral[600],
  fontSize: 13,
});

export const styleOverridesSelectIndicator = () => ({
  cursor: 'pointer',
  color: 'inherit',
  fontSize: 13,

  svg: {
    fontSize: 'inherit',
  },
});

export const styleOverridesTypography = (theme: Theme) => ({
  [`&.${typographyClasses.colorPrimary}`]: {
    color: theme.palette.primary.plainColor,
  },

  // eslint-disable-next-line max-len
  [`&.${typographyClasses['body-sm']}:not(.${typographyClasses.colorPrimary}):not(.${typographyClasses.colorDanger}):not(.${typographyClasses.colorSuccess}):not(.${typographyClasses.colorWarning}):not(.${typographyClasses.colorNeutral})`]:
    {
      color: theme.palette.neutral[800],
    },
});

export const styleOverridesLink = (theme: Theme) => ({
  [`&.${linkClasses.colorPrimary}`]: {
    color: theme.palette.primary.plainColor,
    textDecorationColor: theme.palette.primary.softColor,
  },
});

export const styleOverridesCard = (ownerState: CardOwnerState, theme: Theme) => ({
  ...(ownerState.variant === Variant.PLAIN &&
    ownerState.color === Color.NEUTRAL && {
      backgroundColor: theme.palette.neutral[100],
    }),
  ...(ownerState.size === Size.SM && {
    padding: 12,
    gap: '7px',
  }),
  ...(ownerState.color === Color.WARNING &&
    ownerState.variant === Variant.SOFT && {
      backgroundColor: theme.palette.warning[100],
      color: theme.palette.warning[700],
    }),
});

export const styleOverridesList = (ownerState: ListOwnerState) => ({
  ...(ownerState.size === Size.SM && {
    '--ListItem-paddingY': '0px',
    '--ListItem-minHeight': '12px',
  }),
});

export const styleOverridesTooltip = (ownerState: TooltipOwnerState, theme: Theme) => ({
  padding: '2px 5px',
  borderRadius: 6,
  boxShadow: '0px 2px 4px 0px rgba(21, 21, 21, 0.08), 0px 1px 2px 0px rgba(21, 21, 21, 0.08)',
  maxWidth: 250,
  fontWeight: 500,

  ...(ownerState.color === Color.WARNING &&
    ownerState.variant === Variant.SOFT && {
      backgroundColor: theme.palette.warning[100],
      color: theme.palette.warning[700],
    }),
});

export const styleOverridesAlert = (ownerState: AlertOwnerState, theme: Theme) => ({
  padding: '10px 8px',

  ...(ownerState.color === Color.WARNING &&
    ownerState.variant === Variant.SOFT && {
      backgroundColor: theme.palette.warning[100],
      color: theme.palette.warning[700],
    }),
});

export const styleOverridesSwitch = (ownerState: SwitchOwnerState, theme: Theme) => ({
  ...(ownerState.color === Color.PRIMARY &&
    (ownerState.variant === Variant.SOLID || ownerState.variant === Variant.OUTLINED) &&
    ownerState.checked &&
    ownerState.disabled && {
      '--Switch-trackBackground': `${theme.palette.primary[300]} !important`,
      '--Switch-trackBorderColor': `${theme.palette.primary[300]} !important`,
      '--Switch-thumbBackground': `${theme.palette.common[CustomColor.WHITE]} !important`,
    }),
});

export const styleOverridesModalDialog = (ownerState: ModalDialogOwnerState, theme: Theme) => ({
  ...(ownerState.color === Color.WARNING &&
    ownerState.variant === Variant.SOFT && {
      backgroundColor: theme.palette.warning[100],
      color: theme.palette.warning[700],
    }),
});

export const styleOverridesDialogActions = () => ({
  '& .MuiButton-root:only-child': {
    flex: 'none',
  },
});
